import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import DataTable from '../../components/tables/DataTable';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import StatCard from '../../components/charts/StatCard';
import Toast from '../../components/common/Toast';
import { useToast } from '../../hooks/useToast';
import contractService from '../../services/contractService';
import ViewContractModal from '../../components/features/contracts/ViewContractModal';

const ManagerContractsView = () => {
  const [contracts, setContracts] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') || 'ALL');
  const [filteredContracts, setFilteredContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);

  const { toasts, removeToast, error, success } = useToast();

  useEffect(() => {
    loadContracts();
  }, []);

  useEffect(() => {
    filterContracts();
  }, [searchTerm,statusFilter,contracts]);

  const loadContracts = async () => {
    setLoading(true);
    const result = await contractService.getAllContracts();
    if (result.success) {
      setContracts(result.data);
      setFilteredContracts(result.data);
    } else {
      error(result.error || 'Failed to load Contracts');
    }
    setLoading(false);
  };

  const filterContracts = () => {
    let filtered = [...contracts];

    if (searchTerm) {
      filtered = filtered.filter(c =>
        c.po_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.company?.company_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.site?.name || '').toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter === 'ACTIVE') {
      filtered = filtered.filter(c => c.is_enabled);
    } else if (statusFilter === 'INACTIVE') {
      filtered = filtered.filter(c => !c.is_enabled);
    }

    setFilteredContracts(filtered);
  };

  const handleViewContract = (contract) => {
    setSelectedContract(contract);
    setShowViewModal(true);
  };

  const handleExportContracts = async () => {
  const result = await contractService.exportContractsCSV({ status: statusFilter });
    if (result.success) {
      success('Contracts exported successfully!');
    } else {
      error(result.error || 'Failed to export contracts');
    }
  };

  const getContractStats = () => {
    const total = contracts.length;
    const active = contracts.filter(c => c.is_enabled).length;
    const inactive = contracts.filter(c => !c.is_enabled).length;
    const totalValue = contracts
      .filter(contract =>statusFilter === 'INACTIVE'? !contract.is_enabled: contract.is_enabled)
      .reduce((sum, contract) =>sum + (Number(contract.value) || 0),0);    
      return { total, active, inactive, totalValue };
    };    

  const stats = getContractStats();

  const handleContractCardClick = (filter) => {
    setSearchTerm('');
    setStatusFilter(filter);

    if (filter === 'ALL') {
      setSearchParams({});
    } else {
      setSearchParams({ status: filter });
    }
  };

  const filterBarStyle = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '24px',
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'space-between'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
    marginBottom: '32px'
  };

  const columns = [
    {
      key: 'po_number',
      label: 'PO Number',
      render: (value) => <span style={{ fontWeight: '600', color: 'var(--primary)' }}>{value}</span>
    },
    {
      key: 'company',
      label: 'Company',
      render: (value, row) => row.company?.company_name || row.company_name || 'Unassigned'
    },
    {
      key: 'site_location',
      label: 'Site & Location',
      render: (value, row) => {
        const siteName = row.site?.name || row.site_name || 'Unassigned';
        const location = row.site?.location || row.location || '';
        return (
          <div>
            <div style={{ fontWeight: '500' }}>{siteName}</div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>{location}</div>
          </div>
        );
      }
    },
    {
      key: 'type',
      label: 'Type',
      render: (value) => (
        <span style={{
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: '600',
          background: value === 'Comprehensive' ? '#10b98120' : '#f59e0b20',
          color: value === 'Comprehensive' ? '#10b981' : '#f59e0b'
        }}>
          {value}
        </span>
      )
    },
    {
      key: 'value',
      label: 'Value',
      render: (value) => `₹${Number(value).toLocaleString()}`
    },
    {
      key: 'duration',
      label: 'Duration',
      render: (value, row) => (
        <div style={{ fontSize: '13px' }}>
          <div><strong>Start:</strong> {new Date(row.start_date).toLocaleDateString()}</div> 
          <div><strong>End:</strong> {new Date(row.end_date).toLocaleDateString()}</div>
        </div>
      )
    },
    {
      key: 'is_enabled',
      label: 'Status',
      render: (value) => {
        const isEnabled = value === true;
        return (
          <span style={{
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '600',
            background: isEnabled ? '#10b98120' : '#9ca3af20',
            color: isEnabled ? '#10b981' : '#9ca3af'
          }}>
            {isEnabled ? 'Active' : 'Disabled'}
          </span>
        );
      }
    }
  ];

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-secondary)' }}>Loading Contracts...</div>;
  }

  return (
    <div>
      {toasts.map(toast => (
        <Toast key={toast.id} message={toast.message} type={toast.type} onClose={() => removeToast(toast.id)} />
      ))}

      <div style={gridStyle}>
        <StatCard title="Total Contracts" value={stats.total} icon="" color="#3b82f6" onClick={() => handleContractCardClick('ALL')} />
        <StatCard title="Active Contracts" value={stats.active} icon="" color="#10b981" onClick={() => handleContractCardClick('ACTIVE')} />
        <StatCard title="Inactive Contracts" value={stats.inactive} icon="" color="#f59e0b" onClick={() => handleContractCardClick('INACTIVE')}/>
        <StatCard title="Total Value" value={`₹${(stats.totalValue / 100000).toFixed(2)}L`} icon="" color="#8b5cf6" subtitle="Cumulative value" />
      </div>

      <div style={filterBarStyle}>
        <div style={{ flex: '1', minWidth: '250px', maxWidth: '400px' }}>
          <Input
            placeholder="Search contracts by ID, Company or Site..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
            }
          />
        </div>
        
        <div style={{ display: 'flex', gap: '12px' }}>
          <Button variant="secondary" onClick={loadContracts}>Refresh</Button>
          <Button variant="secondary" onClick={handleExportContracts}>Export CSV</Button>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filteredContracts}
        actions={[
          { label: ' View', onClick: handleViewContract }
        ]}
        pageSize={15}
      />

      <ViewContractModal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedContract(null);
        }}
        contract={selectedContract}
      />
    </div>
  );
};

export default ManagerContractsView;
